import type { Lang } from '../types';

export interface ReservationDetailText {
  loading: string;
  notFound: string;
  back: string;
  pageTitle: string;
  savedSuccess: string;
  collisionStatus: string;
  markAsActive: string;
  markAsInactive: string;
  collisionInactive: string;
  collisionActive: string;
  manuallySet: string;
  pinTitle: string;
  pinSubtitle: string;
  lockOfflineTitle: string;
  lockOfflineBody: string;
  formApartment: string;
  formGuestName: string;
  formCheckInDate: string;
  formCheckOutDate: string;
  formNumberOfGuests: string;
  formStatus: string;
  formNotes: string;
  selectApartment: string;
  statusPending: string;
  statusConfirmed: string;
  statusCheckedIn: string;
  statusCheckedOut: string;
  statusCancelled: string;
  manage: string;
  saving: string;
  save: string;
  delete: string;
  cancel: string;
  confirmDelete: string;
  // Direct booking
  directBookingTitle: string;
  directBookingEmail: string;
  directBookingPhone: string;
  approvingLabel: string;
  approveBtn: string;
  rejectingLabel: string;
  rejectBtn: string;
  confirmApprove: string;
  confirmReject: string;
  approvedSuccess: string;
  rejectedSuccess: string;
  // Overbooked
  overbookedTitle: string;
  overbookedBody: string;
  // Smart lock — PIN present
  regeneratingPin: string;
  regeneratePin: string;
  gettingOffline: string;
  getOfflineCode: string;
  offlineCodeTooltip: string;
  // Smart lock — PIN error / not generated
  pinErrorTitle: string;
  retryPinGeneration: string;
  pinNotGeneratedTitle: string;
  pinNotGeneratedBody: string;
  generatePin: string;
  // Check-in activity timeline
  checkinActivity: string;
  timelineRegistrationStarted: string;
  timelineAllGuestsRegistered: string;
  timelineCityTaxPaid: string;
  timelineGuideOpened: string;
  // PIN modal
  pinModalRegenerateTitle: string;
  pinModalGenerateTitle: string;
  pinModalOfflineTitle: string;
  pinModalOfflineDesc: string;
  pinModalTimeDesc: string;
  pinModalStartDate: string;
  pinModalStartTime: string;
  pinModalEndDate: string;
  pinModalEndTime: string;
  pinModalCancel: string;
  pinModalRegenerateBtn: string;
  pinModalGeneratingLabel: string;
  pinModalGettingLabel: string;
  pinModalGetOfflineBtn: string;
  // Error fallbacks
  errorLoadReservation: string;
  errorSaveReservation: string;
  errorDeleteReservation: string;
  errorSetCode: string;
  errorApproveBooking: string;
  errorRejectBooking: string;
  // City tax manager override
  cityTaxTitle: string;
  cityTaxTitleChanged: string;
  cityTaxTitleOverpaid: string;
  cityTaxTitleExempt: string;
  cityTaxNotPaidYet: string;
  cityTaxExemptBody: string;
  cityTaxPaidAmountLabel: string;
  cityTaxOverridePaid: string;
  cityTaxOverrideExempt: string;
  cityTaxOverrideUnpaid: string;
  cityTaxSetAmountLabel: string;
  cityTaxSetAmountButton: string;
  cityTaxConfirmUnpaid: string;
  cityTaxSetPaidSuccess: string;
  cityTaxSetUnpaidSuccess: string;
  cityTaxSetExemptSuccess: string;
  cityTaxSetError: string;
}

export const reservationDetailText: Record<Lang, ReservationDetailText> = {
  cs: {
    loading: 'Načítám rezervaci…',
    notFound: 'Rezervace nenalezena.',
    back: '← Zpět',
    pageTitle: 'Upravit rezervaci',
    savedSuccess: 'Rezervace úspěšně uložena.',
    collisionStatus: 'Stav konfliktu',
    markAsActive: 'Označit jako aktivní',
    markAsInactive: 'Označit jako neaktivní',
    collisionInactive: 'Neaktivní — konflikt přebukování',
    collisionActive: 'Aktivní',
    manuallySet: 'ručně nastaveno',
    pinTitle: '🔐 PIN chytrého zámku',
    pinSubtitle: 'Vygenerováno pro příjezd · Platné po dobu pobytu',
    lockOfflineTitle: '⚠️ Chytrý zámek offline',
    lockOfflineBody: 'Zámek není připojen k bráně — PIN nelze vygenerovat. Noční úloha se automaticky zkusí znovu, až bude zámek online.',
    formApartment: 'Apartmán',
    formGuestName: 'Jméno hosta',
    formCheckInDate: 'Datum příjezdu',
    formCheckOutDate: 'Datum odjezdu',
    formNumberOfGuests: 'Počet hostů',
    formStatus: 'Stav',
    formNotes: 'Poznámky',
    selectApartment: 'Vyberte apartmán',
    statusPending: 'Čeká',
    statusConfirmed: 'Potvrzeno',
    statusCheckedIn: 'Přijato',
    statusCheckedOut: 'Odhlášeno',
    statusCancelled: 'Zrušeno',
    manage: 'Správa',
    saving: 'Ukládám…',
    save: 'Uložit',
    delete: 'Smazat',
    cancel: 'Zrušit',
    confirmDelete: 'Smazat tuto rezervaci? Tuto akci nelze vrátit zpět.',
    directBookingTitle: 'Poptávka přímé rezervace',
    directBookingEmail: 'E-mail: ',
    directBookingPhone: 'Telefon: ',
    approvingLabel: 'Schvaluji…',
    approveBtn: '✓ Schválit',
    rejectingLabel: 'Zamítám…',
    rejectBtn: '✕ Zamítnout',
    confirmApprove: 'Schválit tuto rezervaci? Host obdrží potvrzovací e-mail.',
    confirmReject: 'Zamítnout tuto rezervaci? Host obdrží e-mail o zamítnutí.',
    approvedSuccess: 'Rezervace schválena. Potvrzovací e-mail odeslán hostovi.',
    rejectedSuccess: 'Rezervace zamítnuta. Oznámení e-mailem odesláno hostovi.',
    overbookedTitle: 'Přebukováno',
    overbookedBody: 'Kalendář s vyšší prioritou má konfliktní rezervaci pro toto období. Změňte pořadí priorit kalendářů v nastavení apartmánu, abyste určili, která rezervace je platná. Přebukovaná rezervace se automaticky neodesílá na Ubyport – pokud host přesto přijede, odešlete jeho údaje ručně.',
    regeneratingPin: 'Obnovuji…',
    regeneratePin: 'Obnovit PIN',
    gettingOffline: 'Získávám…',
    getOfflineCode: '⚡ Offline kód',
    offlineCodeTooltip: 'Cloudový kód, který funguje i bez připojení zámku k bráně',
    pinErrorTitle: 'Chyba PIN chytrého zámku',
    retryPinGeneration: 'Zkusit znovu generovat PIN',
    pinNotGeneratedTitle: 'PIN chytrého zámku nebyl vygenerován',
    pinNotGeneratedBody: 'Generování PIN čeká. Můžete ho spustit ručně.',
    generatePin: 'Generovat PIN',
    checkinActivity: 'Aktivita check-inu',
    timelineRegistrationStarted: 'Registrace zahájena',
    timelineAllGuestsRegistered: 'Všichni hosté zaregistrováni',
    timelineCityTaxPaid: 'Místní poplatek zaplacen',
    timelineGuideOpened: 'Průvodce otevřen',
    pinModalRegenerateTitle: 'Obnovit PIN chytrého zámku',
    pinModalGenerateTitle: 'Generovat PIN chytrého zámku',
    pinModalOfflineTitle: 'Získat offline přístupový kód',
    pinModalOfflineDesc: 'Cloudový kód, který funguje i bez připojení zámku k bráně. ',
    pinModalTimeDesc: 'Nastavte vlastní čas začátku a konce — užitečné pro dřívější uložení zavazadel, brzy check-in nebo pozdní check-out.',
    pinModalStartDate: 'Datum začátku',
    pinModalStartTime: 'Čas začátku',
    pinModalEndDate: 'Datum konce',
    pinModalEndTime: 'Čas konce',
    pinModalCancel: 'Zrušit',
    pinModalRegenerateBtn: 'Obnovit PIN',
    pinModalGeneratingLabel: 'Generuji…',
    pinModalGettingLabel: 'Získávám…',
    pinModalGetOfflineBtn: 'Získat offline kód',
    errorLoadReservation: 'Nepodařilo se načíst rezervaci',
    errorSaveReservation: 'Nepodařilo se uložit rezervaci',
    errorDeleteReservation: 'Nepodařilo se smazat rezervaci',
    errorSetCode: 'Nepodařilo se nastavit kód',
    errorApproveBooking: 'Nepodařilo se schválit rezervaci',
    errorRejectBooking: 'Nepodařilo se zamítnout rezervaci',
    cityTaxTitle: 'Místní poplatek',
    cityTaxTitleChanged: '⚠ Místní poplatek — částka se změnila',
    cityTaxTitleOverpaid: 'ℹ Místní poplatek — přeplatek',
    cityTaxTitleExempt: '— Místní poplatek — bez poplatku',
    cityTaxNotPaidYet: 'Host zatím nezaplatil',
    cityTaxExemptBody: 'Poplatek se nepožaduje.',
    cityTaxPaidAmountLabel: 'Zaplaceno:',
    cityTaxOverridePaid: 'Zaplaceno',
    cityTaxOverrideExempt: 'Bez poplatku',
    cityTaxOverrideUnpaid: 'Nezaplaceno',
    cityTaxSetAmountLabel: 'Nastavit uhrazenou částku:',
    cityTaxSetAmountButton: 'Nastavit',
    cityTaxConfirmUnpaid: 'Opravdu označit místní poplatek jako nezaplacený? Host bude muset zaplatit znovu, než se mu zobrazí průvodce ubytováním.',
    cityTaxSetPaidSuccess: 'Místní poplatek byl označen jako zaplacený.',
    cityTaxSetUnpaidSuccess: 'Místní poplatek byl označen jako nezaplacený.',
    cityTaxSetExemptSuccess: 'U rezervace se místní poplatek nyní nepožaduje.',
    cityTaxSetError: 'Chyba při úpravě stavu platby.',
  },
  en: {
    loading: 'Loading reservation...',
    notFound: 'Reservation not found.',
    back: '← Back',
    pageTitle: 'Edit Reservation',
    savedSuccess: 'Reservation saved successfully.',
    collisionStatus: 'Collision Status',
    markAsActive: 'Mark as active',
    markAsInactive: 'Mark as inactive',
    collisionInactive: 'Inactive — overbooking conflict',
    collisionActive: 'Active',
    manuallySet: 'manually set',
    pinTitle: '🔐 Smart Lock PIN',
    pinSubtitle: 'Generated for check-in · Valid during stay',
    lockOfflineTitle: '⚠️ Smart Lock Offline',
    lockOfflineBody: 'The lock is not connected to a gateway — PIN could not be generated. The nightly job will retry automatically when the lock is back online.',
    formApartment: 'Apartment',
    formGuestName: 'Guest Name',
    formCheckInDate: 'Check-in Date',
    formCheckOutDate: 'Check-out Date',
    formNumberOfGuests: 'Number of Guests',
    formStatus: 'Status',
    formNotes: 'Notes',
    selectApartment: 'Select apartment',
    statusPending: 'Pending',
    statusConfirmed: 'Confirmed',
    statusCheckedIn: 'Checked In',
    statusCheckedOut: 'Checked Out',
    statusCancelled: 'Cancelled',
    manage: 'Manage',
    saving: 'Saving…',
    save: 'Save',
    delete: 'Delete',
    cancel: 'Cancel',
    confirmDelete: 'Delete this reservation? This action cannot be undone.',
    directBookingTitle: 'Direct Booking Inquiry',
    directBookingEmail: 'Email: ',
    directBookingPhone: 'Phone: ',
    approvingLabel: 'Approving…',
    approveBtn: '✓ Approve',
    rejectingLabel: 'Rejecting…',
    rejectBtn: '✕ Reject',
    confirmApprove: 'Approve this booking request? The guest will receive a confirmation email.',
    confirmReject: 'Reject this booking request? The guest will receive a rejection email.',
    approvedSuccess: 'Booking approved. Confirmation email sent to guest.',
    rejectedSuccess: 'Booking rejected. Notification email sent to guest.',
    overbookedTitle: 'Overbooked',
    overbookedBody: 'A higher-priority calendar has a conflicting reservation for this period. Reorder calendar priorities on the apartment settings page to change which reservation wins. An overbooked reservation is not sent to Ubyport automatically – if the guest does arrive, send their details manually.',
    regeneratingPin: 'Regenerating…',
    regeneratePin: 'Regenerate PIN',
    gettingOffline: 'Getting…',
    getOfflineCode: '⚡ Get offline code',
    offlineCodeTooltip: 'Get a cloud-only passcode that works even if the lock has no gateway connection',
    pinErrorTitle: 'Smart Lock PIN Error',
    retryPinGeneration: 'Retry PIN Generation',
    pinNotGeneratedTitle: 'Smart Lock PIN Not Generated',
    pinNotGeneratedBody: 'PIN generation is pending. You can trigger it manually now.',
    generatePin: 'Generate PIN',
    checkinActivity: 'Check-in activity',
    timelineRegistrationStarted: 'Registration started',
    timelineAllGuestsRegistered: 'All guests registered',
    timelineCityTaxPaid: 'City tax paid',
    timelineGuideOpened: 'Guide opened',
    pinModalRegenerateTitle: 'Regenerate Smart Lock PIN',
    pinModalGenerateTitle: 'Generate Smart Lock PIN',
    pinModalOfflineTitle: 'Get Offline Passcode',
    pinModalOfflineDesc: 'A cloud-only passcode that works even if the lock has no gateway connection. ',
    pinModalTimeDesc: 'Set a custom start and end time — useful for early luggage drop-off, early check-in, or a late checkout.',
    pinModalStartDate: 'Start Date',
    pinModalStartTime: 'Start Time',
    pinModalEndDate: 'End Date',
    pinModalEndTime: 'End Time',
    pinModalCancel: 'Cancel',
    pinModalRegenerateBtn: 'Regenerate PIN',
    pinModalGeneratingLabel: 'Generating…',
    pinModalGettingLabel: 'Getting…',
    pinModalGetOfflineBtn: 'Get Offline Code',
    errorLoadReservation: 'Failed to load reservation',
    errorSaveReservation: 'Failed to save reservation',
    errorDeleteReservation: 'Failed to delete reservation',
    errorSetCode: 'Failed to set code',
    errorApproveBooking: 'Failed to approve booking',
    errorRejectBooking: 'Failed to reject booking',
    cityTaxTitle: 'City tax',
    cityTaxTitleChanged: '⚠ City tax — amount changed',
    cityTaxTitleOverpaid: 'ℹ City tax — overpaid',
    cityTaxTitleExempt: '— City tax — exempt',
    cityTaxNotPaidYet: "Guest hasn't paid yet",
    cityTaxExemptBody: 'Tax is not required.',
    cityTaxPaidAmountLabel: 'Paid amount:',
    cityTaxOverridePaid: 'Paid',
    cityTaxOverrideExempt: 'Exempt',
    cityTaxOverrideUnpaid: 'Unpaid',
    cityTaxSetAmountLabel: 'Set paid amount:',
    cityTaxSetAmountButton: 'Set',
    cityTaxConfirmUnpaid: "Mark city tax as unpaid? The guest will need to pay again before the check-in guide unlocks.",
    cityTaxSetPaidSuccess: 'City tax marked as paid.',
    cityTaxSetUnpaidSuccess: 'City tax marked as unpaid.',
    cityTaxSetExemptSuccess: 'City tax is no longer required for this reservation.',
    cityTaxSetError: 'Error updating payment status.',
  },
  vi: {
    loading: 'Đang tải lịch đặt…',
    notFound: 'Không tìm thấy lịch đặt.',
    back: '← Quay lại',
    pageTitle: 'Chỉnh sửa lịch đặt',
    savedSuccess: 'Đã lưu lịch đặt thành công.',
    collisionStatus: 'Trạng thái xung đột',
    markAsActive: 'Đánh dấu là hoạt động',
    markAsInactive: 'Đánh dấu là không hoạt động',
    collisionInactive: 'Không hoạt động — xung đột lịch đặt',
    collisionActive: 'Hoạt động',
    manuallySet: 'đặt thủ công',
    pinTitle: '🔐 PIN khóa thông minh',
    pinSubtitle: 'Tạo cho nhận phòng · Có hiệu lực trong thời gian lưu trú',
    lockOfflineTitle: '⚠️ Khóa thông minh ngoại tuyến',
    lockOfflineBody: 'Khóa không kết nối với cổng — không thể tạo PIN. Tác vụ ban đêm sẽ tự động thử lại khi khóa trực tuyến.',
    formApartment: 'Căn hộ',
    formGuestName: 'Tên khách',
    formCheckInDate: 'Ngày nhận phòng',
    formCheckOutDate: 'Ngày trả phòng',
    formNumberOfGuests: 'Số lượng khách',
    formStatus: 'Trạng thái',
    formNotes: 'Ghi chú',
    selectApartment: 'Chọn căn hộ',
    statusPending: 'Chờ',
    statusConfirmed: 'Đã xác nhận',
    statusCheckedIn: 'Đã nhận phòng',
    statusCheckedOut: 'Đã trả phòng',
    statusCancelled: 'Đã hủy',
    manage: 'Quản lý',
    saving: 'Đang lưu…',
    save: 'Lưu',
    delete: 'Xóa',
    cancel: 'Hủy',
    confirmDelete: 'Xóa lịch đặt này? Hành động này không thể hoàn tác.',
    directBookingTitle: 'Yêu cầu đặt phòng trực tiếp',
    directBookingEmail: 'Email: ',
    directBookingPhone: 'Điện thoại: ',
    approvingLabel: 'Đang duyệt…',
    approveBtn: '✓ Duyệt',
    rejectingLabel: 'Đang từ chối…',
    rejectBtn: '✕ Từ chối',
    confirmApprove: 'Duyệt yêu cầu đặt phòng này? Khách sẽ nhận được email xác nhận.',
    confirmReject: 'Từ chối yêu cầu đặt phòng này? Khách sẽ nhận được email từ chối.',
    approvedSuccess: 'Đã duyệt đặt phòng. Email xác nhận đã gửi đến khách.',
    rejectedSuccess: 'Đã từ chối đặt phòng. Email thông báo đã gửi đến khách.',
    overbookedTitle: 'Đặt phòng vượt mức',
    overbookedBody: 'Lịch có mức độ ưu tiên cao hơn có đặt phòng xung đột trong khoảng thời gian này. Sắp xếp lại ưu tiên lịch trên trang cài đặt căn hộ để xác định đặt phòng nào được ưu tiên. Đặt phòng bị trùng lịch sẽ không được tự động gửi lên Ubyport – nếu khách vẫn đến, hãy gửi thông tin của khách thủ công.',
    regeneratingPin: 'Đang tạo lại…',
    regeneratePin: 'Tạo lại PIN',
    gettingOffline: 'Đang lấy…',
    getOfflineCode: '⚡ Lấy mã offline',
    offlineCodeTooltip: 'Mã đám mây hoạt động ngay cả khi khóa không kết nối với cổng',
    pinErrorTitle: 'Lỗi PIN khóa thông minh',
    retryPinGeneration: 'Thử lại tạo PIN',
    pinNotGeneratedTitle: 'Chưa tạo PIN khóa thông minh',
    pinNotGeneratedBody: 'Đang chờ tạo PIN. Bạn có thể kích hoạt thủ công ngay bây giờ.',
    generatePin: 'Tạo PIN',
    checkinActivity: 'Hoạt động nhận phòng',
    timelineRegistrationStarted: 'Đã bắt đầu đăng ký',
    timelineAllGuestsRegistered: 'Tất cả khách đã đăng ký',
    timelineCityTaxPaid: 'Đã thanh toán phí lưu trú',
    timelineGuideOpened: 'Đã mở hướng dẫn',
    pinModalRegenerateTitle: 'Tạo lại PIN khóa thông minh',
    pinModalGenerateTitle: 'Tạo PIN khóa thông minh',
    pinModalOfflineTitle: 'Lấy mã truy cập offline',
    pinModalOfflineDesc: 'Mã đám mây hoạt động ngay cả khi khóa không kết nối với cổng. ',
    pinModalTimeDesc: 'Đặt thời gian bắt đầu và kết thúc tùy chỉnh — hữu ích cho việc gửi hành lý sớm, nhận phòng sớm hoặc trả phòng muộn.',
    pinModalStartDate: 'Ngày bắt đầu',
    pinModalStartTime: 'Giờ bắt đầu',
    pinModalEndDate: 'Ngày kết thúc',
    pinModalEndTime: 'Giờ kết thúc',
    pinModalCancel: 'Hủy',
    pinModalRegenerateBtn: 'Tạo lại PIN',
    pinModalGeneratingLabel: 'Đang tạo…',
    pinModalGettingLabel: 'Đang lấy…',
    pinModalGetOfflineBtn: 'Lấy mã offline',
    errorLoadReservation: 'Không thể tải lịch đặt',
    errorSaveReservation: 'Không thể lưu lịch đặt',
    errorDeleteReservation: 'Không thể xóa lịch đặt',
    errorSetCode: 'Không thể đặt mã',
    errorApproveBooking: 'Không thể duyệt lịch đặt',
    errorRejectBooking: 'Không thể từ chối lịch đặt',
    cityTaxTitle: 'Phí lưu trú',
    cityTaxTitleChanged: '⚠ Phí lưu trú — số tiền đã thay đổi',
    cityTaxTitleOverpaid: 'ℹ Phí lưu trú — trả thừa',
    cityTaxTitleExempt: '— Phí lưu trú — miễn phí',
    cityTaxNotPaidYet: 'Khách chưa thanh toán',
    cityTaxExemptBody: 'Phí lưu trú không được yêu cầu.',
    cityTaxPaidAmountLabel: 'Đã thanh toán:',
    cityTaxOverridePaid: 'Đã thanh toán',
    cityTaxOverrideExempt: 'Miễn phí',
    cityTaxOverrideUnpaid: 'Chưa thanh toán',
    cityTaxSetAmountLabel: 'Đặt số tiền đã thanh toán:',
    cityTaxSetAmountButton: 'Đặt',
    cityTaxConfirmUnpaid: 'Bạn có chắc muốn đánh dấu phí lưu trú là chưa thanh toán? Khách sẽ phải thanh toán lại trước khi có thể xem hướng dẫn nhận phòng.',
    cityTaxSetPaidSuccess: 'Phí lưu trú đã được đánh dấu là đã thanh toán.',
    cityTaxSetUnpaidSuccess: 'Phí lưu trú đã được đánh dấu là chưa thanh toán.',
    cityTaxSetExemptSuccess: 'Phí lưu trú hiện không được yêu cầu cho lịch đặt này.',
    cityTaxSetError: 'Lỗi khi cập nhật trạng thái thanh toán.',
  },
  uk: {
    loading: 'Завантаження бронювання…',
    notFound: 'Бронювання не знайдено.',
    back: '← Назад',
    pageTitle: 'Редагувати бронювання',
    savedSuccess: 'Бронювання успішно збережено.',
    collisionStatus: 'Статус конфлікту',
    markAsActive: 'Позначити як активне',
    markAsInactive: 'Позначити як неактивне',
    collisionInactive: 'Неактивне — конфлікт перебронювання',
    collisionActive: 'Активне',
    manuallySet: 'встановлено вручну',
    pinTitle: '🔐 PIN розумного замка',
    pinSubtitle: 'Згенеровано для заїзду · Діє протягом перебування',
    lockOfflineTitle: '⚠️ Розумний замок офлайн',
    lockOfflineBody: 'Замок не підключений до шлюзу — PIN не вдалося згенерувати. Нічне завдання автоматично повторить спробу, коли замок буде онлайн.',
    formApartment: 'Апартамент',
    formGuestName: 'Ім\'я гостя',
    formCheckInDate: 'Дата заїзду',
    formCheckOutDate: 'Дата виїзду',
    formNumberOfGuests: 'Кількість гостей',
    formStatus: 'Статус',
    formNotes: 'Нотатки',
    selectApartment: 'Оберіть апартамент',
    statusPending: 'Очікує',
    statusConfirmed: 'Підтверджено',
    statusCheckedIn: 'Заїхав',
    statusCheckedOut: 'Виїхав',
    statusCancelled: 'Скасовано',
    manage: 'Керування',
    saving: 'Збереження…',
    save: 'Зберегти',
    delete: 'Видалити',
    cancel: 'Скасувати',
    confirmDelete: 'Видалити це бронювання? Цю дію неможливо скасувати.',
    directBookingTitle: 'Запит прямого бронювання',
    directBookingEmail: 'Email: ',
    directBookingPhone: 'Телефон: ',
    approvingLabel: 'Підтверджую…',
    approveBtn: '✓ Підтвердити',
    rejectingLabel: 'Відхиляю…',
    rejectBtn: '✕ Відхилити',
    confirmApprove: 'Підтвердити цей запит на бронювання? Гість отримає лист із підтвердженням.',
    confirmReject: 'Відхилити цей запит на бронювання? Гість отримає лист про відмову.',
    approvedSuccess: 'Бронювання підтверджено. Лист із підтвердженням надіслано гостю.',
    rejectedSuccess: 'Бронювання відхилено. Повідомлення надіслано гостю.',
    overbookedTitle: 'Перебронювання',
    overbookedBody: 'Календар із вищим пріоритетом має конфліктне бронювання на цей період. Змініть пріоритети календарів у налаштуваннях квартири, щоб визначити, яке бронювання є дійсним. Бронювання з овербукінгом не надсилається до Ubyport автоматично – якщо гість усе ж приїде, надішліть його дані вручну.',
    regeneratingPin: 'Оновлення…',
    regeneratePin: 'Оновити PIN',
    gettingOffline: 'Отримую…',
    getOfflineCode: '⚡ Офлайн-код',
    offlineCodeTooltip: 'Хмарний код, що працює навіть без підключення замка до шлюзу',
    pinErrorTitle: 'Помилка PIN розумного замка',
    retryPinGeneration: 'Повторити генерацію PIN',
    pinNotGeneratedTitle: 'PIN розумного замка не згенеровано',
    pinNotGeneratedBody: 'Генерація PIN очікує. Ви можете запустити її вручну зараз.',
    generatePin: 'Згенерувати PIN',
    checkinActivity: 'Активність заїзду',
    timelineRegistrationStarted: 'Реєстрацію розпочато',
    timelineAllGuestsRegistered: 'Усіх гостей зареєстровано',
    timelineCityTaxPaid: 'Місцевий податок сплачено',
    timelineGuideOpened: 'Путівник відкрито',
    pinModalRegenerateTitle: 'Оновити PIN розумного замка',
    pinModalGenerateTitle: 'Згенерувати PIN розумного замка',
    pinModalOfflineTitle: 'Отримати офлайн-код доступу',
    pinModalOfflineDesc: 'Хмарний код, що працює навіть без підключення замка до шлюзу. ',
    pinModalTimeDesc: 'Встановіть власний час початку та закінчення — корисно для раннього здачі багажу, раннього заїзду або пізнього виїзду.',
    pinModalStartDate: 'Дата початку',
    pinModalStartTime: 'Час початку',
    pinModalEndDate: 'Дата кінця',
    pinModalEndTime: 'Час кінця',
    pinModalCancel: 'Скасувати',
    pinModalRegenerateBtn: 'Оновити PIN',
    pinModalGeneratingLabel: 'Генерую…',
    pinModalGettingLabel: 'Отримую…',
    pinModalGetOfflineBtn: 'Отримати офлайн-код',
    errorLoadReservation: 'Не вдалося завантажити бронювання',
    errorSaveReservation: 'Не вдалося зберегти бронювання',
    errorDeleteReservation: 'Не вдалося видалити бронювання',
    errorSetCode: 'Не вдалося встановити код',
    errorApproveBooking: 'Не вдалося підтвердити бронювання',
    errorRejectBooking: 'Не вдалося відхилити бронювання',
    cityTaxTitle: 'Місцевий податок',
    cityTaxTitleChanged: '⚠ Місцевий податок — сума змінилася',
    cityTaxTitleOverpaid: 'ℹ Місцевий податок — переплата',
    cityTaxTitleExempt: '— Місцевий податок — без податку',
    cityTaxNotPaidYet: 'Гість ще не сплатив',
    cityTaxExemptBody: 'Податок не вимагається.',
    cityTaxPaidAmountLabel: 'Сплачено:',
    cityTaxOverridePaid: 'Сплачено',
    cityTaxOverrideExempt: 'Без податку',
    cityTaxOverrideUnpaid: 'Не сплачено',
    cityTaxSetAmountLabel: 'Встановити сплачену суму:',
    cityTaxSetAmountButton: 'Встановити',
    cityTaxConfirmUnpaid: 'Дійсно позначити місцевий податок як несплачений? Гостю доведеться сплатити знову, перш ніж зʼявиться путівник по заселенню.',
    cityTaxSetPaidSuccess: 'Місцевий податок позначено як сплачений.',
    cityTaxSetUnpaidSuccess: 'Місцевий податок позначено як несплачений.',
    cityTaxSetExemptSuccess: 'Для цього бронювання місцевий податок більше не вимагається.',
    cityTaxSetError: 'Помилка під час оновлення статусу оплати.',
  },
};
